import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { calculateAll, formatUSD, type CalcItem } from "../utils/acvRcv";

interface ItemBreakdownChartProps {
  items: CalcItem[];
  yearsForward: number;
  reducedMotion?: boolean;
  topN?: number;
}

export default function ItemBreakdownChart({
  items,
  yearsForward,
  reducedMotion,
  topN = 10,
}: ItemBreakdownChartProps) {
  const data = useMemo(() => {
    const results = calculateAll(items, yearsForward);
    return results
      .sort((a, b) => b.gap - a.gap)
      .slice(0, topN)
      .map((r) => ({
        name: r.item.name,
        ACV: Math.round(r.acvTotal),
        Gap: Math.round(r.gap),
      }));
  }, [items, yearsForward, topN]);

  if (data.length === 0) {
    return (
      <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground">
        Add items to see the breakdown.
      </div>
    );
  }

  return (
    <div className="h-[360px] w-full" role="img" aria-label="Top items by insurance gap">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <XAxis
            type="number"
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={130}
            tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--card))",
            }}
            formatter={(value: number, name: string) => [formatUSD(value), name]}
          />
          <Bar
            dataKey="ACV"
            stackId="s"
            fill="hsl(160 65% 42%)"
            isAnimationActive={!reducedMotion}
            animationDuration={700}
          >
            {data.map((_, i) => <Cell key={`a-${i}`} />)}
          </Bar>
          <Bar
            dataKey="Gap"
            stackId="s"
            fill="hsl(348 75% 58%)"
            isAnimationActive={!reducedMotion}
            animationDuration={700}
            radius={[0, 4, 4, 0]}
          >
            {data.map((_, i) => <Cell key={`g-${i}`} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
